import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './VehiculoOdometroModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Lecturas de Odómetro";
const endpoint = ENDPOINTS.vehiculoOdometro;
const endpointById = ENDPOINTS.vehiculoOdometroById;
const modelo = Models.vehiculo;

// Para las lecturas
const modeloLectura = Models.vehiculoOdometro;
const endpointVehiculo = ENDPOINTS.vehiculo;

const maps = {
  mapCreate: (f) => ({
    idVehiculo: Number(f.idVehiculo) || 0,
    idReserva: Number(f.idReserva) || 0,
    fechaLectura: f.fechaLectura,           // 'YYYY-MM-DD'
    kmOdometro: Number(f.kmOdometro) || 0,
    origen: f.origen,
    notas: f.notas,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idLectura: id,
    idVehiculo: Number(f.idVehiculo) || 0,
    idReserva: Number(f.idReserva) || 0,
    fechaLectura: f.fechaLectura,
    kmOdometro: Number(f.kmOdometro) || 0,
    origen: f.origen,
    notas: f.notas,
    activo: !!f.activo,
  }),
};

// Helper para armar la etiqueta de vehículo
function buildVehiculoLabel(idVehiculo, vehiculoMap) {
  if (!idVehiculo) return '';
  const v = vehiculoMap.get(idVehiculo);
  if (!v) return `#${idVehiculo}`;

  const placa = v.placa || 'S/placa';
  const marca = v.marca || '';
  const modelo = v.modelo || '';
  const anio = v.anio || '';
  const detalle = [marca, modelo, anio].filter(Boolean).join(' ');
  return detalle ? `${placa} · ${detalle}` : placa;
}

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      setRows((res.data || []).map((x) => modeloLectura(x)));
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  const loadVehiculos = async () => {
    try {
      const res = await axios.get(endpointVehiculo);
      setVehiculos((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error('Error al cargar vehículos para Odómetro', e);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadVehiculos(); }, []);

  // Mapa idVehiculo → objeto vehículo
  const vehiculoMap = useMemo(() => {
    const m = new Map();
    vehiculos.forEach((v) => {
      if (v.idVehiculo != null) {
        m.set(v.idVehiculo, v);
      }
    });
    return m;
  }, [vehiculos]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const fecha = (r.fechaLectura || '').toLowerCase();
      const origen = (r.origen || '').toLowerCase();
      const notas = (r.notas || '').toLowerCase();
      const vehLabel = buildVehiculoLabel(r.idVehiculo, vehiculoMap).toLowerCase();
      const res = String(r.idReserva ?? '').toLowerCase();
      const km = String(r.kmOdometro ?? '').toLowerCase();
      return (
        fecha.includes(q) || origen.includes(q) || notas.includes(q) ||
        vehLabel.includes(q) || res.includes(q) || km.includes(q)
      );
    });
  }, [rows, query, vehiculoMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idLectura === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idLectura ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por vehículo (placa/marca), fecha, origen, notas, id reserva o km…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Vehículo</th>
                  <th>Id Reserva</th>
                  <th>Fecha lectura</th>
                  <th>KM Odómetro</th>
                  <th>Origen</th>
                  <th>Notas</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const vehLabel = buildVehiculoLabel(r.idVehiculo, vehiculoMap);
                  return (
                    <tr key={r.idLectura}>
                      <td><strong>{normalizeValue(vehLabel)}</strong></td>
                      <td>{normalizeValue(r.idReserva)}</td>
                      <td>{normalizeValue(r.fechaLectura)}</td>
                      <td>{normalizeValue(r.kmOdometro)}</td>
                      <td><strong>{normalizeValue(r.origen)}</strong></td>
                      <td>{normalizeValue(r.notas)}</td>

                      {/* EDITAR con icono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idLectura)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>

                      {/* BORRAR con icono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const name = vehLabel || r.origen || `Lectura ${r.idLectura}`;
                            askDelete(r.idLectura, name);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (<tr><td colSpan={10}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
